import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixIamVerificationV1StartResponse from './iam-verification-v1-start-response-verification.http.js';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

const TRANSFORM_PATHS_0 = [
  { path: 'identity.identityProfile.addresses.address' },
];

export interface StartResponse {
  /** the identifier of the verification process */
  verificationId?: string;
}

export interface StartRequest {
  /**
   * ID of the identity whose email address should be verified. The verification code is sent to this identity's
   * email. Currently required — the request fails if it is omitted.
   * @format GUID
   */
  identityId?: string | null;
  /** Delivery channel for the verification code. Currently only `EMAIL` is supported. */
  target?: TargetWithLiterals;
}

/** The delivery channel used to send the verification code. */
export enum Target {
  /** Send the verification code to the identity's email address. */
  EMAIL = 'EMAIL',
}

/** @enumType */
export type TargetWithLiterals = Target | 'EMAIL';

export interface VerifyRequest {
  /**
   * the code to verify
   * @minLength 6
   * @maxLength 6
   */
  code?: string;
  /** the identifier of the verification process */
  verificationId?: string;
}

export interface VerifyResponse {}

export interface VerifyDuringAuthenticationRequest {
  /**
   * The code to verify.
   * @minLength 6
   * @maxLength 6
   */
  code: string;
  /** A state token representing the `REQUIRE_EMAIL_VERIFICATION` state. */
  stateToken: string;
}

export interface StateMachineResponse
  extends StateMachineResponseStateDataOneOf {
  /** @internal */
  requireMfaData?: RequireMfaData;
  /**
   * User needs to solve an MFA challenge, such as providing the code that sent to them by SMS.
   * @internal
   */
  mfaChallengeData?: MfaChallengeData;
  /**
   * Identity must prove a first factor before the IdP connection is merged.
   * @internal
   */
  requireFirstFactorData?: RequireFirstFactorData;
  /** Current state of the login or registration process. */
  state?: StateTypeWithLiterals;
  /** Session token. If `state` is not `SUCCESS`, this field is undefined. */
  sessionToken?: string | null;
  /** Token that represents the current state of the login or registration process. */
  stateToken?: string | null;
  /** Identity of the current member. */
  identity?: Identity;
  /** Additional data relevant to the login or registration process. */
  additionalData?: Record<string, CustomValue>;
}

/** @oneof */
export interface StateMachineResponseStateDataOneOf {
  /** @internal */
  requireMfaData?: RequireMfaData;
  /**
   * User needs to solve an MFA challenge, such as providing the code that sent to them by SMS.
   * @internal
   */
  mfaChallengeData?: MfaChallengeData;
  /**
   * Identity must prove a first factor before the IdP connection is merged.
   * @internal
   */
  requireFirstFactorData?: RequireFirstFactorData;
}

export enum StateType {
  /** Initial unknown state. */
  UNKNOWN_STATE = 'UNKNOWN_STATE',
  /** Login completed successfully. */
  SUCCESS = 'SUCCESS',
  /** Member needs the owner to approve their registration. */
  REQUIRE_OWNER_APPROVAL = 'REQUIRE_OWNER_APPROVAL',
  /** Member needs to verify their email. */
  REQUIRE_EMAIL_VERIFICATION = 'REQUIRE_EMAIL_VERIFICATION',
  /** The `status` is not one that prevents the member logging in, meaning it's not `OFFLINE`, `BLOCKED`, or `DELETED`. */
  STATUS_CHECK = 'STATUS_CHECK',
  /**
   * Multi-factor authentication is required to complete login.
   * @internal
   */
  REQUIRE_MFA = 'REQUIRE_MFA',
  /**
   * Indicates that the member needs to verify themselves using the selected verication factor type.
   * @internal
   */
  MFA_CHALLENGE = 'MFA_CHALLENGE',
  /**
   * User needs to provide the first factor authentication (e.g., password, webAuthN).
   * @internal
   */
  REQUIRE_FIRST_FACTOR = 'REQUIRE_FIRST_FACTOR',
  /** Member must submit the profile-completion form to continue registration. */
  REQUIRE_PROFILE_COMPLETION = 'REQUIRE_PROFILE_COMPLETION',
  /**
   * Registering identity must prove their sign-up factor (e.g. an emailed code) before the identity is created.
   * @internal
   */
  REQUIRE_REGISTER_CHALLENGE = 'REQUIRE_REGISTER_CHALLENGE',
  /**
   * Identity logging in must enter the code sent to their email before the login continues.
   * @internal
   */
  REQUIRE_LOGIN_CHALLENGE = 'REQUIRE_LOGIN_CHALLENGE',
}

/** @enumType */
export type StateTypeWithLiterals =
  | StateType
  | 'UNKNOWN_STATE'
  | 'SUCCESS'
  | 'REQUIRE_OWNER_APPROVAL'
  | 'REQUIRE_EMAIL_VERIFICATION'
  | 'STATUS_CHECK'
  | 'REQUIRE_PROFILE_COMPLETION';

export interface Identity {
  /**
   * Identity ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the identity is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the identity.
   *
   * Ignored when creating an identity.
   */
  revision?: string | null;
  /**
   * Date and time the identity was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the identity was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** The identity configured connections to authenticate with. */
  connections?: Connection[];
  /** Identity profile. */
  identityProfile?: IdentityProfile;
  /**
   * Additional information about the identity that can impact user access.
   * This data cannot be set.
   */
  metadata?: Metadata;
  /** Identity email address. */
  email?: Email;
  /** Identity's current status. */
  status?: StatusV2;
  /**
   * Custom attributes.
   * @internal
   */
  customAttributes?: Record<string, any> | null;
  /**
   * Identity verification factors.
   * @maxSize 10
   * @readonly
   */
  factors?: Factor[];
}

export interface Connection extends ConnectionTypeOneOf {
  /** IDP connection. */
  idpConnection?: IdpConnection;
  /** Authenticator connection. */
  authenticatorConnection?: AuthenticatorConnection;
}

/** @oneof */
export interface ConnectionTypeOneOf {
  /** IDP connection. */
  idpConnection?: IdpConnection;
  /** Authenticator connection. */
  authenticatorConnection?: AuthenticatorConnection;
}

export interface IdpConnection {
  /**
   * IDP connection ID.
   * @format GUID
   */
  idpConnectionId?: string;
  /** IDP user ID. */
  idpUserId?: string;
}

export interface AuthenticatorConnection {
  /**
   * Authenticator connection ID.
   * @format GUID
   */
  authenticatorConnectionId?: string;
  /** Whether re-enrollment is required. */
  reEnrollmentRequired?: boolean;
}

export interface IdentityProfile {
  /** Member's first name. */
  firstName?: string | null;
  /** Member's last name. */
  lastName?: string | null;
  /** Member's display nickname. */
  nickname?: string | null;
  /** URL to member's profile picture. */
  picture?: string | null;
  /**
   * Deprecated. Use `secondaryEmails` instead.
   * @deprecated Deprecated. Use `secondaryEmails` instead.
   * @replacedBy secondary_emails
   * @targetRemovalDate 2023-11-01
   */
  emails?: string[];
  /**
   * Deprecated. Use `phonesV2` instead.
   * @deprecated Deprecated. Use `phonesV2` instead.
   * @replacedBy phones_v2
   * @targetRemovalDate 2023-11-01
   */
  phones?: string[];
  /**
   * Labels associated with the member.
   * @maxSize 2000
   */
  labels?: string[];
  /**
   * Member's preferred language code.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Member's profile visibility setting. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Any number of custom fields. [Custom fields](https://support.wix.com/en/article/adding-custom-fields-to-contacts)
   * are used to store additional information about your site or app's contacts.
   */
  customFields?: CustomField[];
  /** Additional email addresses for the member. */
  secondaryEmails?: SecondaryEmail[];
  /** Phone numbers associated with the member. */
  phonesV2?: Phone[];
  /** Physical addresses for the member. */
  addresses?: AddressWrapper[];
  /**
   * Company name.
   * @maxLength 1000
   */
  company?: string | null;
  /**
   * Position within company.
   * @maxLength 1000
   */
  position?: string | null;
  /**
   * Member's birth date in `YYYY-MM-DD` format.
   * @format LOCAL_DATE
   */
  birthdate?: string | null;
  /**
   * URL-friendly slug for the member's profile.
   * @maxLength 255
   */
  slug?: string | null;
  /** Whether the member consents to receive marketing communications. */
  subscription?: boolean | null;
  /**
   * Tax ID or VAT number for personal or corporate use.
   * @maxLength 1000
   */
  vatId?: string | null;
}

export enum PrivacyStatus {
  /** No privacy status defined. */
  UNDEFINED = 'UNDEFINED',
  /** Profile details are visible to other members. */
  PUBLIC = 'PUBLIC',
  /** Profile details are hidden from other members. */
  PRIVATE = 'PRIVATE',
}

/** @enumType */
export type PrivacyStatusWithLiterals =
  | PrivacyStatus
  | 'UNDEFINED'
  | 'PUBLIC'
  | 'PRIVATE';

export interface CustomField {
  /**
   * Custom field name matching an extended field key (without `custom.` prefix).
   *
   * Must match a key from [`List Extended Fields`](https://dev.wix.com/docs/rest/api-reference/contacts/extended-fields/list-extended-fields).
   */
  name?: string;
  /** Value for the custom field. */
  value?: V1CustomValue;
}

export interface V1CustomValue extends V1CustomValueValueOneOf {
  /** Text value. */
  strValue?: string;
  /** Numeric value. */
  numValue?: number;
  /** Date/time value. */
  dateValue?: Date | null;
  /** List of values. */
  listValue?: V1ListValue;
  /** Key-value pairs. */
  mapValue?: V1MapValue;
  /** Bool value */
  boolValue?: boolean;
}

/** @oneof */
export interface V1CustomValueValueOneOf {
  /** Text value. */
  strValue?: string;
  /** Numeric value. */
  numValue?: number;
  /** Date/time value. */
  dateValue?: Date | null;
  /** List of values. */
  listValue?: V1ListValue;
  /** Key-value pairs. */
  mapValue?: V1MapValue;
  /** Bool value */
  boolValue?: boolean;
}

export interface V1ListValue {
  /** List of custom values. */
  value?: V1CustomValue[];
}

export interface V1MapValue {
  /** Map of custom field values. */
  value?: Record<string, V1CustomValue>;
}

export interface SecondaryEmail {
  /**
   * Secondary email address.
   * @format EMAIL
   */
  email?: string;
  /** Email tag. */
  tag?: EmailTagWithLiterals;
}

export enum EmailTag {
  UNTAGGED = 'UNTAGGED',
  MAIN = 'MAIN',
  HOME = 'HOME',
  WORK = 'WORK',
}

/** @enumType */
export type EmailTagWithLiterals =
  | EmailTag
  | 'UNTAGGED'
  | 'MAIN'
  | 'HOME'
  | 'WORK';

export interface Phone {
  /**
   * Country code for the phone number.
   * @format COUNTRY
   */
  countryCode?: string | null;
  /**
   * Phone number without country code.
   * @format PHONE
   */
  phone?: string;
  /** Phone tag. */
  tag?: PhoneTagWithLiterals;
}

export enum PhoneTag {
  UNTAGGED = 'UNTAGGED',
  MAIN = 'MAIN',
  HOME = 'HOME',
  MOBILE = 'MOBILE',
  WORK = 'WORK',
  FAX = 'FAX',
}

/** @enumType */
export type PhoneTagWithLiterals =
  | PhoneTag
  | 'UNTAGGED'
  | 'MAIN'
  | 'HOME'
  | 'MOBILE'
  | 'WORK'
  | 'FAX';

export interface AddressWrapper {
  /** Physical address details. */
  address?: Address;
  /** Address tag. */
  tag?: AddressTagWithLiterals;
}

/** Physical address */
export interface Address {
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /** Free text providing more detailed address info. Usually contains apartment, suite, and floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formatted?: string | null;
}

export enum AddressTag {
  UNTAGGED = 'UNTAGGED',
  HOME = 'HOME',
  WORK = 'WORK',
  BILLING = 'BILLING',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type AddressTagWithLiterals =
  | AddressTag
  | 'UNTAGGED'
  | 'HOME'
  | 'WORK'
  | 'BILLING'
  | 'SHIPPING';

export interface Metadata {
  /**
   * General tags. For example, `"isOwner"`.
   * @internal
   * @readonly
   */
  tags?: string[];
}

export interface Email {
  /**
   * Email address.
   * @format EMAIL
   */
  address?: string;
  /** Whether the email address is verified. */
  isVerified?: boolean;
}

export interface StatusV2 {
  /** Descriptive name of the identity status. */
  name?: StatusNameWithLiterals;
  /** Reasons for the current identity status. */
  reasons?: ReasonWithLiterals[];
}

export enum StatusName {
  /** Unknown status. This value isn't used. */
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  /** Identity is pending verification, owner approval, or both. */
  PENDING = 'PENDING',
  /** Identity is active and can log in. */
  ACTIVE = 'ACTIVE',
  /** Identity is deleted. This identity can't log in. */
  DELETED = 'DELETED',
  /** Identity is blocked. This identity can't log in. */
  BLOCKED = 'BLOCKED',
  /** Identity is offline. This identity can't log in. */
  OFFLINE = 'OFFLINE',
}

/** @enumType */
export type StatusNameWithLiterals =
  | StatusName
  | 'UNKNOWN_STATUS'
  | 'PENDING'
  | 'ACTIVE'
  | 'DELETED'
  | 'BLOCKED'
  | 'OFFLINE';

export enum Reason {
  /** Unknown reason. This value isn't used. */
  UNKNOWN_REASON = 'UNKNOWN_REASON',
  /** The identity is waiting for admin approval. After the approval, the identity has an `ACTIVE` status. */
  PENDING_ADMIN_APPROVAL_REQUIRED = 'PENDING_ADMIN_APPROVAL_REQUIRED',
  /** The identity has to verify the email. After the verification, the identity has an `ACTIVE` status. */
  PENDING_EMAIL_VERIFICATION_REQUIRED = 'PENDING_EMAIL_VERIFICATION_REQUIRED',
  /** The identity must complete the site's profile-completion form. Other pending requirements may remain after completion. */
  PENDING_PROFILE_COMPLETION_REQUIRED = 'PENDING_PROFILE_COMPLETION_REQUIRED',
}

/** @enumType */
export type ReasonWithLiterals =
  | Reason
  | 'UNKNOWN_REASON'
  | 'PENDING_ADMIN_APPROVAL_REQUIRED'
  | 'PENDING_EMAIL_VERIFICATION_REQUIRED'
  | 'PENDING_PROFILE_COMPLETION_REQUIRED';

export interface Factor {
  /**
   * Factor ID.
   * @format GUID
   */
  factorId?: string;
  /** Factor type. */
  type?: FactorTypeWithLiterals;
  /** Factor status. */
  status?: StatusWithLiterals;
}

export enum FactorType {
  /** Requires a password. */
  PASSWORD = 'PASSWORD',
  /** Requires a code sent via SMS. */
  SMS = 'SMS',
  /** Requires a code sent by phone call. */
  CALL = 'CALL',
  /** Requires a code sent by email. */
  EMAIL = 'EMAIL',
  /** Requires authentication via an authenticator app. */
  TOTP = 'TOTP',
  /** Requires authentication via a push notification. */
  PUSH = 'PUSH',
  /** Requires authentication via WebAuthn/passkey. */
  WEBAUTHN = 'WEBAUTHN',
  /** Requires a recovery code. */
  RECOVERY_CODE = 'RECOVERY_CODE',
}

/** @enumType */
export type FactorTypeWithLiterals =
  | FactorType
  | 'PASSWORD'
  | 'SMS'
  | 'CALL'
  | 'EMAIL'
  | 'TOTP'
  | 'PUSH'
  | 'WEBAUTHN'
  | 'RECOVERY_CODE';

export enum Status {
  /** Factor requires activation. */
  INACTIVE = 'INACTIVE',
  /** Factor is active and can be used for authentication. */
  ACTIVE = 'ACTIVE',
  /** Factor is blocked and cannot be used for authentication. The user should reenroll the factor. */
  REQUIRE_REENROLL = 'REQUIRE_REENROLL',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'INACTIVE'
  | 'ACTIVE'
  | 'REQUIRE_REENROLL';

export interface CustomValue extends CustomValueValueOneOf {
  /** String value. */
  strValue?: string;
  /** Number value. */
  numValue?: number;
  /** Date value. */
  dateValue?: Date | null;
  /** List value. */
  listValue?: ListValue;
  /** Map value. */
  mapValue?: MapValue;
}

/** @oneof */
export interface CustomValueValueOneOf {
  /** String value. */
  strValue?: string;
  /** Number value. */
  numValue?: number;
  /** Date value. */
  dateValue?: Date | null;
  /** List value. */
  listValue?: ListValue;
  /** Map value. */
  mapValue?: MapValue;
}

export interface ListValue {
  /** Custom value. */
  value?: CustomValue[];
}

export interface MapValue {
  /** Mapped custom value. */
  value?: Record<string, CustomValue>;
}

export interface RequireMfaData {
  /**
   * The factors available for the user to perform the required MFA.
   * @maxSize 100
   */
  availableFactors?: V1Factor[];
  /** The reason the user is required to perform MFA. */
  reason?: MfaReasonWithLiterals;
}

export interface V1Factor {
  /** Type of verification factor. */
  factorType?: FactorTypeWithLiterals;
  /** Availability status for second factor */
  factorStatus?: FactorStatusWithLiterals;
}

export enum FactorStatus {
  ENABLED = 'ENABLED',
  REQUIRE_ACTIVATION = 'REQUIRE_ACTIVATION',
  REQUIRE_REENROLL = 'REQUIRE_REENROLL',
  ENABLED_BY_RULE = 'ENABLED_BY_RULE',
  DISABLED_BY_RULE = 'DISABLED_BY_RULE',
}

/** @enumType */
export type FactorStatusWithLiterals =
  | FactorStatus
  | 'ENABLED'
  | 'REQUIRE_ACTIVATION'
  | 'REQUIRE_REENROLL'
  | 'ENABLED_BY_RULE'
  | 'DISABLED_BY_RULE';

export enum MfaReason {
  /** Mfa is required due to user settings */
  USER_SETTINGS = 'USER_SETTINGS',
  /** Mfa is required due to high risk login */
  HIGH_RISK_LOGIN = 'HIGH_RISK_LOGIN',
}

/** @enumType */
export type MfaReasonWithLiterals =
  | MfaReason
  | 'USER_SETTINGS'
  | 'HIGH_RISK_LOGIN';

export interface MfaChallengeData {
  /** Type of verifation factor. */
  factorType?: FactorTypeWithLiterals;
  verificationChallengeData?: VerificationChallenge;
  /**
   * Factors types the user can choose from to verify themselves.
   * @maxSize 100
   */
  availableFactors?: V1Factor[];
  /** The reason the user is required for verification. */
  reason?: MfaReasonWithLiterals;
}

export interface VerificationChallenge
  extends VerificationChallengeFactorChallengeDataOneOf {
  pushData?: PushChallengeData;
  webauthnData?: WebAuthnChallengeData;
  /** @maxLength 200 */
  hint?: string | null;
}

/** @oneof */
export interface VerificationChallengeFactorChallengeDataOneOf {
  pushData?: PushChallengeData;
  webauthnData?: WebAuthnChallengeData;
}

export interface PushChallengeData {
  /**
   * This should be tied to a specific push notification.
   * It will be sent in subsequent requests to verify the factor.
   * @maxLength 36
   */
  transactionId?: string;
}

export interface WebAuthnChallengeData {
  /**
   * A unique identifier for this challenge that will be used to correlate
   * the authentication response with this challenge.
   * This should be a cryptographically random GUID to prevent guessing attacks.
   * @format GUID
   */
  challengeId?: string;
  /**
   * A PublicKeyCredentialRequestOptions JSON as defined in the WebAuthn spec (https://www.w3.org/TR/webauthn-3/#dictdef-publickeycredentialrequestoptions).
   * @maxLength 40000
   */
  publicKeyCredentialRequestOptionsJson?: string;
}

export interface RequireFirstFactorData {
  /**
   * Organization the identity is under.
   * @format GUID
   */
  organizationId?: string;
}

/** @docsIgnore */
export type VerifyDuringAuthenticationApplicationErrors = {
  code?: 'VERIFICATION_FAILED';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Starts an email verification process by sending a verification code to the identity's email address.
 * The code is sent to the email associated with the provided `identityId`, and the returned `verificationId`
 * identifies the verification process.
 *
 * Email verification is required when the registering member is already listed as a contact, or when it
 * is required by the site's member settings.
 *
 * During registration or authentication, the member then completes verification by calling
 * `VerifyDuringAuthentication` with the `state_token` of the `REQUIRE_EMAIL_VERIFICATION` state — not the
 * `verificationId` returned here, which `VerifyDuringAuthentication` does not accept.
 * @public
 * @permissionId IAM.START
 * @fqn wix.iam.verification.v1.VerificationService.Start
 */
export async function start(
  options?: StartOptions
): Promise<NonNullablePaths<StartResponse, `verificationId`, 0>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    identityId: options?.identityId,
    target: options?.target,
  });

  const reqOpts = ambassadorWixIamVerificationV1StartResponse.start(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          identityId: '$[0].identityId',
          target: '$[0].target',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface StartOptions {
  /**
   * ID of the identity whose email address should be verified. The verification code is sent to this identity's
   * email. Currently required — the request fails if it is omitted.
   * @format GUID
   */
  identityId?: string | null;
  /** Delivery channel for the verification code. Currently only `EMAIL` is supported. */
  target?: TargetWithLiterals;
}

/**
 * Continues the registration process when a member is required to verify an email address
 * using a verification code received by email.
 *
 * Email verification is required when the registering member is already listed as a contact.
 *
 * Typically, after a successful verification, you generate and use member tokens for the
 * registered member so that subsequent API calls are called as part of a member session.
 * @param code - The code to verify.
 * @public
 * @requiredField code
 * @requiredField options
 * @requiredField options.stateToken
 * @fqn wix.iam.verification.v1.VerificationService.VerifyDuringAuthentication
 */
export async function verifyDuringAuthentication(
  code: string,
  options: NonNullablePaths<VerifyDuringAuthenticationOptions, `stateToken`, 0>
): Promise<
  NonNullablePaths<
    StateMachineResponse,
    | `state`
    | `identity.connections`
    | `identity.connections.${number}.idpConnection.idpConnectionId`
    | `identity.connections.${number}.idpConnection.idpUserId`
    | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
    | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
    | `identity.identityProfile.emails`
    | `identity.identityProfile.phones`
    | `identity.identityProfile.labels`
    | `identity.identityProfile.privacyStatus`
    | `identity.identityProfile.customFields`
    | `identity.identityProfile.customFields.${number}.name`
    | `identity.identityProfile.customFields.${number}.value.strValue`
    | `identity.identityProfile.customFields.${number}.value.numValue`
    | `identity.identityProfile.customFields.${number}.value.boolValue`
    | `identity.identityProfile.secondaryEmails`
    | `identity.identityProfile.secondaryEmails.${number}.email`
    | `identity.identityProfile.secondaryEmails.${number}.tag`
    | `identity.identityProfile.phonesV2`
    | `identity.identityProfile.phonesV2.${number}.phone`
    | `identity.identityProfile.phonesV2.${number}.tag`
    | `identity.identityProfile.addresses`
    | `identity.identityProfile.addresses.${number}.tag`
    | `identity.email.address`
    | `identity.email.isVerified`
    | `identity.status.name`
    | `identity.status.reasons`
    | `identity.factors`
    | `identity.factors.${number}.factorId`
    | `identity.factors.${number}.type`
    | `identity.factors.${number}.status`,
    5
  > & {
    __applicationErrorsType?: VerifyDuringAuthenticationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    code: code,
    stateToken: options?.stateToken,
  });

  const reqOpts =
    ambassadorWixIamVerificationV1StartResponse.verifyDuringAuthentication(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: TRANSFORM_PATHS_0,
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          code: '$[0]',
          stateToken: '$[1].stateToken',
        },
        singleArgumentUnchanged: false,
      },
      ['code', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface VerifyDuringAuthenticationOptions {
  /** A state token representing the `REQUIRE_EMAIL_VERIFICATION` state. */
  stateToken: string;
}
